import { useGoogleLogin } from '@react-oauth/google';
import { Button } from 'antd';
import { jwtDecode } from 'jwt-decode';
import { useEffect } from 'react';

import { Icon, Text, canUseDOM, useTheme } from '@desygner/ui-common-components';

import { getSocialProviders } from '../../AuthModal.data';
import { getSocialProvidersButtonStyles } from '../../AuthModal.styles';
import {
  LoginDtoT,
  SocialProviderT,
  SocialProvidersCompactViewPropsT,
} from '../../AuthModal.types';
import { getServerErrorMessage, getWindowOrigin } from '../../AuthModal.utils';
import { useAuthContext } from '../../context/AuthContext';
import {
  StyledSocialProvider,
  StyledSocialProvidersList,
  StyledSocialProvidersRow,
  StyledSocialProvidersWrapper,
} from './SocialProviders.style';

// Social provider button
export const SocialProviderButton = ({ ...props }: SocialProviderT) => {
  const theme = useTheme();

  return (
    <Button
      onClick={props.onClick}
      style={getSocialProvidersButtonStyles(theme)}
      data-action={props.action}
    >
      <StyledSocialProvider $isAppleIcon={props.id === 'sp-apple'}>
        {props.icon && <Icon name={props.icon} color={theme.Color.Neutrals.Dark} />}
        <Text size="Body">{props.name}</Text>
      </StyledSocialProvider>
    </Button>
  );
};

// Compact view - only first two and another way - DO NOT EXPORT - ONLY USED IN SocialProvidersView
const SocialProvidersCompactView = ({ ...props }: SocialProvidersCompactViewPropsT) => {
  const { isLightTheme } = useAuthContext();

  useEffect(() => {
    if (!canUseDOM) return;

    const script = document.createElement('script');

    script.type = 'text/javascript';
    script.src =
      'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

    script.async = true;

    script.onload = () => {
      if ((window as any)?.AppleID) {
        (window as any)?.AppleID.auth.init({
          clientId: 'com.fluer.login.web',
          scope: 'email name',
          redirectURI: getWindowOrigin(),
          usePopup: true,
        });
      }
    };

    document.body.appendChild(script);

    // Cleanup function to remove the script when the component unmounts
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <StyledSocialProvidersWrapper>
      <StyledSocialProvidersRow>
        {/* CONTINUE WITH GOOGLE */}
        <SocialProviderButton
          id="sp-google"
          name="Google"
          icon="brand-google"
          onClick={() => props.onGoogleLogin?.()}
          action="auth-google-login"
        />

        {/* CONTINUE WITH APPLE - dynamicAppleIcon */}
        <SocialProviderButton
          id="sp-apple"
          name="Apple"
          icon={isLightTheme ? 'brand---apple-store' : 'brand---apple-store-light'}
          onClick={() => props.onAppleLogin?.()}
          action="auth-apple-login"
        />
        {/* </AppleLogin> */}
      </StyledSocialProvidersRow>

      {/* CONTINUE ANOTHER WAY */}
      {/* <SocialProviderButton
        id="sp-other"
        name={props.anotherWayLabel}
        onClick={() => props.onChangeView?.('anotherWay')}
      /> */}
    </StyledSocialProvidersWrapper>
  );
};

// Full list view - all providers - DO NOT EXPORT - ONLY USED IN SocialProvidersView
const SocialProvidersList: React.FC<{ continueWithLabel?: string }> = ({
  continueWithLabel: continueWith,
}) => {
  const providers = getSocialProviders().slice(0, 2);

  return (
    <StyledSocialProvidersList>
      {providers.map((provider) => (
        <SocialProviderButton
          key={provider.id}
          {...provider}
          name={`${continueWith ?? ''} ${provider.name}`}
        />
      ))}
    </StyledSocialProvidersList>
  );
};

export type SocialProvidersViewPropsT = {
  isCompact?: boolean;
  anotherWayLabel?: string;
  continueWithLabel?: string;
};

export const SocialProvidersView = ({ ...props }: SocialProvidersViewPropsT) => {
  const {
    apiLogin,
    setServerError,
    onLogin,
    onClose,
    setIsLoadingView,
    setIsAuthCloseTemporarilyDisabled,
    invitationToken,
  } = useAuthContext();

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      const payload: LoginDtoT = {
        auth_code: codeResponse.code,
        auth_type: 'google_web',
        redirect_url: getWindowOrigin(),
        invitation_token: invitationToken,
      };

      setIsAuthCloseTemporarilyDisabled?.(true);
      setIsLoadingView?.(true);
      await apiLogin?.(payload)
        .then((res) => {
          if (!res.data.token) {
            const tokenError = 'User token is ' + res?.data?.token;

            console.error(tokenError);
            setServerError?.(tokenError);
            return;
          }

          onLogin?.(res.data.token);
          onClose?.();
        })
        .catch((err) => {
          setServerError?.(getServerErrorMessage(err) ?? 'Google login error');
        })
        .finally(() => {
          // Delay to prevent flickering
          setTimeout(() => {
            setIsLoadingView?.(false);
            setIsAuthCloseTemporarilyDisabled?.(false);
          }, 750);
        });
    },
    onError: (error) => {
      console.error('Google login error', error);
      setServerError?.(getServerErrorMessage(error) ?? 'Google login error');
    },
    // redirect_uri: getWindowHref(),
    redirect_uri: getWindowOrigin(),
    flow: 'auth-code',
  });

  const handleAppleLogin = async () => {
    await (window as any)?.AppleID?.auth
      .signIn()
      .then(async (res: any) => {
        setIsAuthCloseTemporarilyDisabled?.(true);
        setIsLoadingView?.(true);
        const { sub } = jwtDecode(res?.authorization?.id_token);

        const payload: LoginDtoT = {
          email: res?.user?.email,
          auth_type: 'apple',
          auth_token: res?.authorization?.code,
          auth_id: sub,
          invitation_token: invitationToken,
        };

        await apiLogin?.(payload)
          .then((res) => {
            if (!res.data.token) {
              const tokenError = 'User token is ' + res?.data?.token;

              console.error(tokenError);
              setServerError?.(tokenError);
              return;
            }

            onLogin?.(res.data.token);
            onClose?.();
          })
          .catch((err) => {
            setServerError?.(getServerErrorMessage(err) ?? 'Apple login error');
          })
          .finally(() => {
            // Delay to prevent flickering
            setTimeout(() => {
              setIsAuthCloseTemporarilyDisabled?.(false);
              setIsLoadingView?.(false);
            }, 750);
          });
      })
      .catch((err: any) => {
        setServerError?.(getServerErrorMessage(err) ?? 'Apple login error');
      });
  };

  return props.isCompact ? (
    <SocialProvidersCompactView
      anotherWayLabel={props.anotherWayLabel}
      onGoogleLogin={() => handleGoogleLogin()}
      onAppleLogin={() => handleAppleLogin()}
      continueWithLabel={props.continueWithLabel}
    />
  ) : (
    <SocialProvidersList continueWithLabel={props.continueWithLabel} />
  );
};
